/**
 * Backend URLs for the self-service feature, supplied at runtime from the builder
 * JSON config's `apiEndpoints` block (see SelfServiceApiConfigService).
 *
 * This module does NOT import the app `environment` — URLs come from JSON only.
 * That is the dependency-inversion that lets the feature live in the shared
 * library (where the app environment isn't available). The host app is
 * responsible for putting the correct per-environment URLs into the builder JSON
 * (`assets/self-service-config/*-builder.json` → `apiEndpoints`).
 */
export interface SelfServiceApiOverrides {
  /** API base URL (required for any API call to resolve). */
  baseUrl?: string;
  /** Multi-file upload URL (falls back to baseUrl + /csv/upload-multiple). */
  uploadUrl?: string;
  /** Chat / generate URL (falls back to baseUrl + the generate path). */
  chatUrl?: string;
  /** Schema-list URL (falls back to baseUrl + `/schemas`). */
  schemaListUrl?: string;
  /** Workflow-list URL (falls back to baseUrl + `/workflow/list`). */
  workflowListUrl?: string;
}

let apiOverrides: SelfServiceApiOverrides = {};

/** Set the runtime URLs (called once when the JSON config loads). */
export function setSelfServiceApiOverrides(overrides: SelfServiceApiOverrides | undefined | null): void {
  if (!overrides) {
    apiOverrides = {};
    return;
  }

  apiOverrides = {
    ...apiOverrides,
    baseUrl: normalizeConfiguredApiUrl(overrides.baseUrl, apiOverrides.baseUrl) || apiOverrides.baseUrl,
    uploadUrl: normalizeConfiguredApiUrl(overrides.uploadUrl, apiOverrides.baseUrl) || apiOverrides.uploadUrl,
    chatUrl: normalizeConfiguredApiUrl(overrides.chatUrl, apiOverrides.baseUrl) || apiOverrides.chatUrl,
    schemaListUrl: normalizeConfiguredApiUrl(overrides.schemaListUrl, apiOverrides.baseUrl) || apiOverrides.schemaListUrl,
    workflowListUrl: normalizeConfiguredApiUrl(overrides.workflowListUrl, apiOverrides.baseUrl) || apiOverrides.workflowListUrl,
  };
}

function normalizeConfiguredApiUrl(url: string | undefined | null, baseUrl: string | undefined): string | undefined {
  const configured = String(url ?? '').trim();
  if (!configured) return undefined;
  if (/^https?:\/\//i.test(configured)) return stripTrailingSlash(configured);

  const base = stripTrailingSlash(String(baseUrl ?? '').trim());
  if (!base || !/^https?:\/\//i.test(base)) return configured;

  const path = configured.startsWith('/') ? configured : `/${configured}`;
  const baseOrigin = getUrlOrigin(base);
  const baseWithoutPlatform = base.replace(/\/spriced\/platform$/i, '');
  return path.startsWith('/spriced/platform')
    ? `${baseOrigin || baseWithoutPlatform}${path}`
    : `${base}${path}`;
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function getUrlOrigin(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

export function getSelfServiceBaseUrl(): string {
  const configured = apiOverrides.baseUrl?.trim() ?? '';
  if (!configured) {
    // JSON `apiEndpoints.baseUrl` is mandatory now (no environment fallback).
    // Warn loudly so a missing/empty config is obvious in dev; API calls will be
    // relative (and likely fail) until the host app fills it in.
    console.warn(
      '[self-service] apiEndpoints.baseUrl is not set in the builder JSON config — ' +
        'API calls will not resolve. Set assets/self-service-config/*-builder.json → apiEndpoints.baseUrl.',
    );
    return '';
  }
  return stripTrailingSlash(configured);
}

/** Kaveri controllers whose @RequestMapping omits the /spriced/platform prefix,
 *  so these paths must resolve against the base URL's origin instead. Sibling
 *  /api paths (e.g. /api/dvm/self-service, /api/deam) ARE under /spriced/platform
 *  and must not be listed here. */
const ROOT_MOUNTED_API_PREFIXES = [
  '/api/entities/',
  '/api/ppt-dashboard',
  '/api/gemma',
  '/api/shape-builder-components',
  '/api/assist/workflow-builder',
];

function isRootMountedApiPath(path: string): boolean {
  return ROOT_MOUNTED_API_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix));
}

const PLATFORM_ALIASED_API_PREFIXES: ReadonlyArray<[string, string]> = [
  ['/api/components', '/components'],
];

function toPlatformAliasedPath(path: string): string | null {
  for (const [rootPrefix, platformPrefix] of PLATFORM_ALIASED_API_PREFIXES) {
    if (path === rootPrefix || path.startsWith(`${rootPrefix}/`) || path.startsWith(`${rootPrefix}?`)) {
      return `${platformPrefix}${path.slice(rootPrefix.length)}`;
    }
  }
  return null;
}

export function buildSelfServiceApiUrl(path: string): string {
  const baseUrl = getSelfServiceBaseUrl();
  // An empty path must return the bare base URL untouched — `''.startsWith('/')`
  // is false, so without this guard it fell through to `/${''}` = '/', tacking a
  // stray trailing slash onto the base (e.g. ".../spriced/platform" became
  // ".../spriced/platform/", 404ing against a backend that doesn't expect it).
  if (!path) return getSelfServicePlatformApiUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const platformAliasedPath = toPlatformAliasedPath(normalizedPath);
  if (platformAliasedPath) {
    return `${getSelfServicePlatformApiUrl()}${platformAliasedPath}`;
  }
  if (isRootMountedApiPath(normalizedPath)) {
    return buildSelfServiceRootApiUrl(normalizedPath);
  }
  if (/\/spriced\/platform$/i.test(baseUrl) && normalizedPath.startsWith('/spriced/platform')) {
    return `${baseUrl.replace(/\/spriced\/platform$/i, '')}${normalizedPath}`;
  }
  return `${baseUrl}${normalizedPath}`;
}

/** Kaveri controllers mounted at the server root (`@RequestMapping("/api/...")`)
 *  rather than under `/spriced/platform`. Their URLs resolve against the base
 *  URL's origin, so the `/spriced/platform` segment is not prepended. */
export function buildSelfServiceRootApiUrl(path: string): string {
  const baseUrl = getSelfServiceBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const root = getUrlOrigin(baseUrl) || baseUrl.replace(/\/spriced\/platform$/i, '');
  return `${root}${normalizedPath}`;
}

export function getSelfServiceComponentComputeApiUrl(): string {
  return buildSelfServiceApiUrl('/api/components/compute');
}

export function resolveSelfServiceApiUrl(configuredUrl: string | undefined | null, fallbackPath = ''): string {
  const configured = String(configuredUrl ?? '').trim();
  if (!configured) return buildSelfServiceApiUrl(fallbackPath);
  if (/^https?:\/\//i.test(configured)) return configured;
  return buildSelfServiceApiUrl(configured);
}
export function getSelfServiceUploadApiUrl(): string {
  const override = apiOverrides.uploadUrl?.trim();
  if (override) {
    return override;
  }
  return buildSelfServiceApiUrl('/csv/upload-multiple');
}

export function getSelfServiceWorkflowDeleteFilesApiUrl(): string {
  return buildSelfServiceApiUrl('/workflow/delete/files');
}

export function getSelfServiceWorkflowUploadApiUrl(): string {
  return buildSelfServiceApiUrl('/workflow/upload');
}

export function getSelfServiceWorkflowRunApiUrl(workflowId: string): string {
  const normalizedWorkflowId = encodeURIComponent(String(workflowId || '').trim());
  return buildSelfServiceApiUrl(`/workflow/run/${normalizedWorkflowId}`);
}

export function getSelfServiceDeamApiUrl(schema = 'public'): string {
  const platformBaseUrl = getSelfServicePlatformApiUrl();
  const deamBaseUrl = platformBaseUrl.replace(/\/spriced\/platform$/i, '/spriced/deam');
  const normalizedSchema = encodeURIComponent(String(schema || 'public').trim() || 'public');
  return `${deamBaseUrl}/getDEAM?schema=${normalizedSchema}`;
}

export function getSelfServicePlatformGetDataApiUrl(): string {
  return `${getSelfServicePlatformApiUrl()}/getData`;
}

export function getSelfServicePlatformApiUrl(): string {
  const baseUrl = getSelfServiceBaseUrl();
  return /\/spriced\/platform$/i.test(baseUrl)
    ? baseUrl
    : buildSelfServiceApiUrl('/spriced/platform');
}

export function getSelfServiceEntityColumnsUpdateApiUrl(entityName: string): string {
  const normalizedEntityName = encodeURIComponent(String(entityName || '').trim());
  return buildSelfServiceRootApiUrl(`/api/entities/${normalizedEntityName}/columns/update`);
}

export function getSelfServiceChatApiUrl(): string {
  const override = apiOverrides.chatUrl?.trim();
  if (override) {
    return resolveSelfServiceApiUrl(override);
  }
  return buildSelfServiceRootApiUrl('/api/ppt-dashboard/generate');
}

/** Schema-list URL for the Data Builder's "Schema" dropdown. Override via
 *  builder JSON `apiEndpoints.schemaListUrl`; falls back to `baseUrl + /schemas`
 *  when unset (adjust the fallback path once the real backend route is
 *  confirmed). */
export function getSelfServiceSchemaListApiUrl(): string {
  const override = apiOverrides.schemaListUrl?.trim();
  if (override) {
    return resolveSelfServiceApiUrl(override);
  }
  return buildSelfServiceApiUrl('/schemas');
}

/** Workflow-list URL for the Data Builder's "Workflow" dropdown. Override via
 *  builder JSON `apiEndpoints.workflowListUrl`; falls back to
 *  `baseUrl + /workflow/list` when unset (adjust the fallback path once the
 *  real backend route is confirmed). */
export function getSelfServiceWorkflowListApiUrl(): string {
  const override = apiOverrides.workflowListUrl?.trim();
  if (override) {
    return resolveSelfServiceApiUrl(override);
  }
  return buildSelfServiceApiUrl('/workflow/list');
}

export function getSelfServiceGeneratedDashboardSaveApiUrl(
  dashboardSlug: string,
  workflow = 'fulfillment',
): string {
  const normalizedSlug = encodeURIComponent(String(dashboardSlug || '').trim());
  const normalizedWorkflow = encodeURIComponent(String(workflow || 'fulfillment').trim() || 'fulfillment');
  return buildSelfServiceRootApiUrl(`/api/ppt-dashboard/generated/${normalizedSlug}/${normalizedWorkflow}`);
}

export function unwrapArray<T>(response: unknown, keys: string[] = ['items', 'data', 'results', 'charts', 'dashboards']): T[] {
  if (Array.isArray(response)) {
    return response as T[];
  }

  if (response && typeof response === 'object') {
    const record = response as Record<string, unknown>;
    for (const key of keys) {
      if (Array.isArray(record[key])) {
        return record[key] as T[];
      }
    }
  }

  return [];
}

export function unwrapObject<T>(response: unknown, keys: string[] = ['data', 'item', 'chart', 'dashboard']): T | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const record = response as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as T;
    }
  }

  return record as T;
}

export function extractStringArray(values: unknown): string[] {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((value) => String(value ?? '').trim())
    .filter((value) => value.length > 0);
}

